import React, { useState, useEffect } from 'react';
import { shape, PropTypes } from 'prop-types';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import './InternalJobsContainer.scss';
import isIE from 'ps-components/Utils/checkForIE';
import Search from 'ps-components/Search/Search';
import JobSearchResults from '../../JobSearchResults/JobSearchResults';
import InternalJobsResults from './InternalJobsResults/InternalJobsResults';
import { getInternalJobs, getJobSurveys, resetInternalJobsReducer, updateSearch } from './actions/InternalJobsActions';
import { useMasterAccountId, useMarketPricing } from '../../../hooks/redux';
import { useStickyState, useDidMountEffect } from '../../../hooks/util';

function InternalJobsContainer(props) {
  const {
    useJobCodeKey,
    replacementTokens,
    selectedSurveys,
    currentJob,
    internalJobsGridActions,
    onMatchSelect,
    togglePopover,
    renderPopover,
    selectedRowId,
    onDataCutClick,
    getSurveyJobDetailsPageHref,
    updateModifiedDate,
    isAdHoc,
    showMinHeader,
    toggleHeader,
    refreshJobTabData,
    id,
    currency,
    viewLoading,
    hideSelect
  } = props;

  const [scopeValue, setScopeValue] = useStickyState(false, id + '_facet_open');
  const [open, setOpen] = useState(scopeValue ? scopeValue === 'true' : true);
  const dispatch = useDispatch();
  const marketPricing = useMarketPricing();
  const masterAccountId = useMasterAccountId();

  const {
    limit,
    offset,
    searchQuery,
    selectedJob,
    typedSearchQuery,
    internalJobList,
    searchResultsCount
  } = marketPricing.InternalJobsReducer.InternalJobsMainReducer;

  const { displayIn } = marketPricing.MarketPricingReducer;
  const selectedViewPk = marketPricing.MarketPricingReducer.surveySelectedView?.selectedViewPk;
  const { selectedBenchmarkYear } = marketPricing.JobPricingReducer.MainJobPricingReducer;

  useEffect(() => {
    pageJobs(limit, offset);
  }, []);

  useDidMountEffect(() => {
    pageJobs(limit, 0, true);
  }, [currency]);

  useEffect(() => {
    if (selectedJob && selectedViewPk !== null) {
      onJobSelect(selectedJob);
    }
  }, [selectedViewPk, displayIn]);

  // Pagination handler
  function pageJobs(limit, offset, keepSelectedJob) {
    dispatch(getInternalJobs(searchQuery, currency, limit, offset)).then(internalJobList => {
      if (keepSelectedJob) {
        return;
      }
      if (!selectedJob || (selectedJob && !internalJobList.find(job => job.orgJobId === selectedJob.orgJobId))) {
        let job = internalJobList[0];
        if (!job) return;
        if (parseInt(job.orgJobId) === parseInt(currentJob.id) && internalJobList.length > 1) {
          job = internalJobList[1];
        }
        if (job) {
          onJobSelect(job);
        } else {
          dispatch(resetInternalJobsReducer());
        }
      } else if (selectedJob) {
        onJobSelect(selectedJob);
      }
    });
  }

  // Start search bar handling functions
  function onSearchChange(input) {
    dispatch(updateSearch(input.target.value));
  }

  function clearSearch() {
    dispatch(updateSearch('')).then(() => {
      dispatch(getInternalJobs('', currency, limit, 0)).then(() => {
        if (
          internalJobList[0] &&
          parseInt(internalJobList[0].orgJobId) === parseInt(currentJob.id) &&
          internalJobList.length > 1
        ) {
          onJobSelect(internalJobList[1]);
        } else {
          onJobSelect(internalJobList[0]);
        }
      });
    });
  }

  function onSearchKeyDown(input) {
    if (input.key === 'Enter') {
      dispatch(getInternalJobs(typedSearchQuery, currency, limit, 0)).then(() => {
        if (
          internalJobList[0] &&
          parseInt(internalJobList[0].orgJobId) === parseInt(currentJob.id) &&
          internalJobList.length > 1
        ) {
          onJobSelect(internalJobList[1]);
        } else {
          onJobSelect(internalJobList[0]);
        }
      });
    }
  }
  // End search bar handling functions

  // onSelect of job
  function onJobSelect(job) {
    if (!job || parseInt(job.orgJobId) === parseInt(currentJob.id)) {
      return;
    }

    dispatch(getJobSurveys(job, selectedBenchmarkYear, displayIn, internalJobsGridActions.search, masterAccountId));
  }

  function toggleFacetPanel() {
    const newOpenState = !open;
    setScopeValue(newOpenState);
    setOpen(newOpenState);
  }

  return (
    <div className="internal-jobs__container">
      <div className={classnames('internal-jobs__sidemenu', { closed: !open, open: open })}>
        <div
          title={open ? 'Close company job search' : 'Open company job search'}
          className={classnames('internal-jobs__close-button', {
            close: !open,
            open: open,
            ie: isIE()
          })}
          onClick={toggleFacetPanel}
        >
          <i className="icon-right-open" />
        </div>
        <div className="internal-jobs__search">
          <Search
            value={typedSearchQuery}
            onClick={clearSearch}
            onChange={onSearchChange}
            onKeyDown={onSearchKeyDown}
            placeholder="Search company jobs"
          />
        </div>
        <JobSearchResults
          className={classnames('internal-jobs__sidemenu-results', { min: showMinHeader, adhoc: isAdHoc })}
          paginationContainerClass="internal-jobs__pagination-container"
          useJobCodeKey={useJobCodeKey}
          hasResults={internalJobList?.length > 0}
          onSelectJob={onJobSelect}
          orgJobs={internalJobList}
          selectedJobId={selectedJob?.orgJobId}
          offset={offset}
          totalCount={searchResultsCount}
          onPageOrgJobs={pageJobs}
          replacementTokens={replacementTokens}
          currentOpenJobId={currentJob.id}
        />
      </div>
      <InternalJobsResults
        className="internal-jobs__results"
        sidepanelOpen={open}
        orgJobsLength={internalJobList.length}
        useJobCodeKey={useJobCodeKey}
        replacementTokens={replacementTokens}
        selectedBenchmarkYear={selectedBenchmarkYear}
        selectedSurveys={selectedSurveys}
        updateModifiedDate={updateModifiedDate}
        currentJob={currentJob}
        displayIn={displayIn}
        internalJobsGridActions={internalJobsGridActions}
        onMatchSelect={onMatchSelect}
        viewLoading={viewLoading}
        hideSelect={hideSelect}
        onDataCutClick={onDataCutClick}
        togglePopover={togglePopover}
        renderPopover={renderPopover}
        selectedRowId={selectedRowId}
        getSurveyJobDetailsPageHref={getSurveyJobDetailsPageHref}
        isAdHoc={isAdHoc}
        showMinHeader={showMinHeader}
        toggleHeader={toggleHeader}
        refreshJobTabData={refreshJobTabData}
        marketPricing={marketPricing}
      />
    </div>
  );
}

InternalJobsContainer.propTypes = {
  currentJob: shape({
    id: PropTypes.number,
    code: PropTypes.string,
    codeKey: PropTypes.string
  }),
  selectedSurveys: PropTypes.array,
  replacementTokens: PropTypes.array,
  useJobCodeKey: PropTypes.bool,
  currency: PropTypes.string,
  internalJobsGridActions: PropTypes.object,
  onMatchSelect: PropTypes.func,
  selectedViewPk: PropTypes.number,
  viewLoading: PropTypes.bool,
  togglePopover: PropTypes.func,
  renderPopover: PropTypes.bool,
  selectedRowId: PropTypes.number,
  hideSelect: PropTypes.bool,
  onDataCutClick: PropTypes.func,
  updateModifiedDate: PropTypes.func,
  isAdHoc: PropTypes.bool,
  showMinHeader: PropTypes.bool,
  toggleHeader: PropTypes.func,
  getSurveyJobDetailsPageHref: PropTypes.func,
  id: PropTypes.string,
  refreshJobTabData: PropTypes.func
};

export default InternalJobsContainer;
